import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Badge, Spinner, Text } from '@/components/primitives';
import { Icons } from '@/theme';
import { useStream } from '@/features/profile/hooks/useStream';
import { useStreamUiStore } from '@/store';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { AnchoredCard, type AnchorRect } from './AnchoredCard';
import { NotificationsPanel } from './NotificationsPanel';
import { bellButton, linkButton } from './notificationBell.css';

/**
 * The transient "your export is on its way" notice — a passive card under the
 * bell, dismissed only by its timer or by opening the panel. No backdrop, so a
 * closing modal dialog can't snatch it away.
 */
function StreamNotice({ anchor }: { anchor: AnchorRect | null }) {
  const noticeOpen = useStreamUiStore((s) => s.noticeOpen);
  const noticeMessage = useStreamUiStore((s) => s.noticeMessage);
  const openPanel = useStreamUiStore((s) => s.openPanel);

  if (!noticeOpen || !anchor) return null;

  return (
    <AnchoredCard
      anchor={anchor}
      width={300}
      zIndex={2100}
      role="status"
      aria-label="Activity update"
      data-testid="stream-queued-notice"
    >
      <div aria-live="polite" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14 }}>
        <Spinner size={18} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Text variant="body" as="span">
            {noticeMessage}
          </Text>
          <button type="button" className={linkButton} style={{ alignSelf: 'flex-start' }} onClick={openPanel}>
            View activity
          </button>
        </div>
      </div>
    </AnchoredCard>
  );
}

/**
 * Top-level notifications bell — the activity stream's home in the header,
 * sitting between the user menu and the help icon and styled to match the help
 * icon (same hit area, near-white glyph, hover fade; size passed in so it
 * tracks the help icon's responsive height).
 *
 * It anchors two surfaces: the transient queued-job notice and the
 * notifications panel, both portalled beneath the bell with a caret pointing at
 * it so they read as belonging to the bell.
 */
export function NotificationBell({ size = 26 }: { size?: number }) {
  // The stream is context-scoped: every /api/stream/* route resolves the
  // caller's org/network context and answers 400 "No organization or network
  // context selected" without one (vingreact#64 — reported as a client_crash on
  // first SSO login, then as 255x condition 28 in prod). `useStream` gates its
  // queries on the same signal, but the gate belongs to the BELL too: a bell
  // with no context has nothing to show and nothing to poll, so it renders
  // nothing at all and no future mount site can reintroduce the poll.
  const { currentContext } = useCurrentUser();
  const hasContext = currentContext !== null;

  const { unreadCount } = useStream();
  const panelOpen = useStreamUiStore((s) => s.panelOpen);
  const noticeOpen = useStreamUiStore((s) => s.noticeOpen);
  const openPanel = useStreamUiStore((s) => s.openPanel);
  const closePanel = useStreamUiStore((s) => s.closePanel);

  const bellRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const measure = useCallback(() => {
    const el = bellRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ bottom: r.bottom, right: r.right, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (panelOpen || noticeOpen) measure();
  }, [panelOpen, noticeOpen, measure]);

  // Keep the panel pinned to the bell if the viewport changes while it's open.
  useEffect(() => {
    if (!panelOpen) return;
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    return () => window.removeEventListener('resize', onChange);
  }, [panelOpen, measure]);

  if (!hasContext) return null;

  return (
    <>
      <button
        ref={bellRef}
        type="button"
        className={bellButton}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={panelOpen}
        data-testid="notifications-bell"
        onClick={() => (panelOpen ? closePanel() : openPanel())}
      >
        <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} max={99} intent="danger">
          <Icons.notifications style={{ fontSize: size, display: 'block' }} />
        </Badge>
      </button>

      <StreamNotice anchor={anchor} />
      <NotificationsPanel anchor={anchor} />
    </>
  );
}
